import { movies } from "@/mocks/movies";
import type { Movie, MovieType } from "@/types/movie";

export interface CategoryConfig {
  key: string;
  label: string;
  type: MovieType | null;
  description: string;
}

export const CATEGORIES: CategoryConfig[] = [
  {
    key: "tv",
    label: "电视剧",
    type: "电视剧",
    description:
      "汇集“激情丛林”系列及同类型高分电视剧，覆盖都市、悬疑、情感等题材，支持高清在线观看。",
  },
  {
    key: "movie",
    label: "电影",
    type: "电影",
    description:
      "精选冒险、爱情、科幻等类型电影，与“激情丛林”同源的丛林题材作品一站畅看。",
  },
  {
    key: "hot",
    label: "热门推荐",
    type: null,
    description:
      "站点播放热度最高的影视内容集合，第一时间发现大家都在追的“激情丛林”相关佳作。",
  },
];

export const getCategory = (key?: string): CategoryConfig =>
  CATEGORIES.find((item) => item.key === key) ?? CATEGORIES[0];

export const REGIONS = [
  "全部",
  "中国大陆",
  "中国香港",
  "中国台湾",
  "美国",
  "韩国",
  "日本",
  "泰国",
  "英国",
];

export const YEARS = ["全部", "2025", "2024", "2023", "2022"];

export const RATING_LEVELS = [
  { label: "全部", min: 0 },
  { label: "9分以上", min: 9 },
  { label: "8分以上", min: 8 },
  { label: "7分以上", min: 7 },
];

export const getMovieById = (id?: string): Movie | undefined =>
  movies.find((item) => item.id === id);

export const getAllMovies = (): Movie[] => movies;

export const getMoviesByCategory = (config: CategoryConfig): Movie[] => {
  if (!config.type) {
    return [...movies].sort((a, b) => b.popularity - a.popularity);
  }
  return movies.filter((item) => item.type === config.type);
};

export const searchMovies = (keyword: string): Movie[] => {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return movies.filter((item) => {
    const hitTitle = item.title.toLowerCase().includes(query);
    const hitIntro = item.intro.toLowerCase().includes(query);
    const hitGenre = item.genres.some((g) => g.toLowerCase().includes(query));
    const hitActors = item.actors.some((a) => a.toLowerCase().includes(query));
    return hitTitle || hitIntro || hitGenre || hitActors;
  });
};

export type SortKey = "update" | "popular" | "name";

export const sortMovies = (list: Movie[], key: SortKey): Movie[] => {
  const cloned = [...list];
  if (key === "popular") {
    return cloned.sort((a, b) => b.popularity - a.popularity);
  }
  if (key === "name") {
    return cloned.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
  }
  return cloned.sort(
    (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime(),
  );
};

export interface FilterState {
  region: string;
  year: string;
  rating: string;
  sort: SortKey;
}

export const filterMovies = (
  list: Movie[],
  filters: Pick<FilterState, "region" | "year" | "rating">,
): Movie[] => {
  const ratingMin = RATING_LEVELS.find((r) => r.label === filters.rating)?.min ?? 0;
  return list.filter((item) => {
    const okRegion = filters.region === "全部" || item.region === filters.region;
    const okYear = filters.year === "全部" || item.year === filters.year;
    const okRating = item.rating >= ratingMin;
    return okRegion && okYear && okRating;
  });
};

export const getRelatedMovies = (movie: Movie, limit = 4): Movie[] => {
  const sameType = movies.filter(
    (item) => item.id !== movie.id && item.type === movie.type,
  );
  const scored = [...sameType].sort((a, b) => {
    const aScore = a.genres.filter((g) => movie.genres.includes(g)).length;
    const bScore = b.genres.filter((g) => movie.genres.includes(g)).length;
    if (aScore !== bScore) return bScore - aScore;
    return b.popularity - a.popularity;
  });
  if (scored.length >= limit) return scored.slice(0, limit);
  const extra = movies
    .filter((item) => item.id !== movie.id && !scored.includes(item))
    .sort((a, b) => b.popularity - a.popularity);
  return [...scored, ...extra].slice(0, limit);
};

export const getFeaturedMovies = (): Movie[] =>
  movies.filter((item) => item.featured);

export const getLatestMovies = (limit = 8): Movie[] =>
  sortMovies(movies, "update").slice(0, limit);

export const getHotMovies = (limit = 8): Movie[] =>
  sortMovies(movies, "popular").slice(0, limit);

export const getEditorPicks = (limit = 4): Movie[] =>
  [...movies].sort((a, b) => b.rating - a.rating).slice(0, limit);

export const formatPopularity = (value: number): string => {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return value.toString();
};